import { type ReactNode, createContext, useContext } from 'react'

/**
 * i18n infrastructure, shipped at setup per the "retrofit is painful" decision
 * (PLAN-00 OQ-4 / DR-4) even though nothing is translated in v1.
 *
 * This is a deliberately thin, Lingui-compatible shim: it exposes the same
 * `t` / `<Trans>` / `<I18nProvider>` surface module code calls, backed by an
 * identity catalog (no translation). The convention "no hardcoded user-facing
 * strings - wrap in t / <Trans>" holds today; swapping in the full Lingui SWC
 * macro + catalog compiler later is a localized change to this file plus the
 * Vite plugin. See docs/00-overview/decisions.md (DR-4).
 */

export type Locale = 'en'

interface I18nContextValue {
  locale: Locale
  /** Translate a message. Identity in v1 (returns the source string). */
  t(message: string): string
}

const defaultI18n: I18nContextValue = {
  locale: 'en',
  t: (message) => message,
}

const I18nContext = createContext<I18nContextValue>(defaultI18n)

export interface I18nProviderProps {
  locale?: Locale
  children: ReactNode
}

export function I18nProvider({ locale = 'en', children }: I18nProviderProps) {
  return (
    <I18nContext.Provider value={{ locale, t: defaultI18n.t }}>{children}</I18nContext.Provider>
  )
}

/** Hook form: `const { t } = useLingui()`. */
export function useLingui(): I18nContextValue {
  return useContext(I18nContext)
}

/**
 * Module-level `t` for non-component callsites and the common
 * `t\`Some text\`` / `t('Some text')` shapes. Identity in v1.
 */
export function t(message: TemplateStringsArray | string, ...substitutions: unknown[]): string {
  if (typeof message === 'string') return message
  // Tagged-template form: interleave the static parts with substitutions.
  return message.reduce((acc, part, i) => {
    const sub = i < substitutions.length ? String(substitutions[i]) : ''
    return acc + part + sub
  }, '')
}

export interface TransProps {
  children: ReactNode
}

/** `<Trans>...</Trans>` - renders its children as-is in v1 (identity catalog). */
export function Trans({ children }: TransProps) {
  return <>{children}</>
}
